import { ACCESS_TYPES } from "@/constants/access-constants";
import { AREA_TYPE_TYPE, getCountsCategory, TYPE_PER_AREA_TYPE } from "@/constants/system-constants";
import { useTabCount } from "@/hooks/useCategoryTab";
import SystemStat from "@/models/SystemStat";
import { getSystemStats } from "@/services/systemService";
import { RouterRootState } from "connected-react-router";
import React, { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import AreasTab from "../../../containers/access-areas-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Button } from "../../../components/button/Button";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { Filter } from "../../../models/Filter";
import { showError } from "../../../services/utilities/ServiceError";
import { getListOfSiteIdsFromFilter } from "../../../utilities/tabUtils";

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

interface AccessAreasProps {
    selected: number;
    setSelected: React.Dispatch<React.SetStateAction<number>>;
}

const AccessArea: FC<AccessAreasProps> = ({ selected, setSelected }) => {
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const { translate } = useLocale();
    const [categories, setCategories] = useState<Category[]>([
        {
            displayName: translate.byKey(TranslationKeys.accessAreas),
            heading: translate.byKey(TranslationKeys.accessAreas),
            value: "",
            id: 1,
            count: 0,
        },
        {
            displayName: translate.byKey(TranslationKeys.liftAreas),
            heading: translate.byKey(TranslationKeys.liftAreas),
            value: "",
            id: 2,
            count: 0,
        },
    ]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const { updateTabCount } = useTabCount({
        tableName: "Area",
        categories,
        setCategories,
        customFilters: {
            areaType: `=${selected === 0 ? ACCESS_TYPES.ACCESS_AREA : ACCESS_TYPES.FLOOR_AREA}`
        }
    });

    useEffect(() => {
        getCategoriesTotals();
    }, []);

    const getCategoriesTotals = async () => {
        try {
            setBusy(true);
            const showTabCounts = window.sessionStorage.getItem("showTabCounts") === "1";
            const systemStats: SystemStat | undefined = showTabCounts ? await getSystemStats(TYPE_PER_AREA_TYPE) : undefined;

            const updatedCategories = categories.map(category => {
                if (category.id === 1) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, AREA_TYPE_TYPE.ACCESS_AREA) : 0 };
                }
                else if (category.id === 2) {
                    return { ...category, count: systemStats ? getCountsCategory(systemStats, AREA_TYPE_TYPE.LIFT_AREA) : 0 };
                }

                return category;
            });

            setCategories(updatedCategories);
        } catch (error) {
            showError(error, toast.warn);
        } finally {
            setBusy(false);
        }
    }

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent = [
                <AreasTab
                    key={"accessAreaTab"}
                    setBusy={setBusy}
                    onFilterChange={onFilterChange}
                    getCategoriesTotals={getCategoriesTotals}
                    areaType={1} />,
                <AreasTab
                    key={"liftAreaTab"}
                    setBusy={setBusy}
                    onFilterChange={onFilterChange}
                    getCategoriesTotals={getCategoriesTotals}
                    areaType={2} />
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const onFilterChange = (filters: Filter[]) => {
        updateTabCount(getListOfSiteIdsFromFilter(filters));
    }

    return <ActivityFrame busy={busy} className="flex flex-grow overflow-y-hidden">
        <div id="container-tabs" className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
            <Breadcrumb
                path={[
                    {
                        value: "/access-control/enrolment",
                        displayName: translate.byKey(TranslationKeys.enrolments),
                    },
                    {
                        value: "/access-control/enrolment/access-areas",
                        displayName: translate.byKey(TranslationKeys.accessAreas),
                    },
                ]}
                onClick={(item) => history.push(item.value)} />
            <Tabs
                className="overflow-y-hidden"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)}
                selected={selected}
                onAdd={() => { }}
                add={false}>
                {tabContent[selected]}
                {selected === 0 && <div className="flex flex-col flex-grow overflow-y-hidden mb-viewport">
                    <div className="flex flex-row justify-end button-cluster">
                        <Restricted to={`add.${routeName}.element`}>
                            <Link
                                to={{
                                    pathname:
                                        "/access-control/enrolment/access-area/",
                                }}>
                                <Button variant="submit" shape="rounded" id="addBtn">
                                    <img
                                        src={plusIcon}
                                        className="px-2 w-8 py-1"
                                        alt="" />
                                    <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                                </Button>
                            </Link>
                        </Restricted>
                    </div>
                </div>}
            </Tabs>
        </div>
    </ActivityFrame>
};

export default AccessArea;
